import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";
import { ChatCompletionRequest } from "../../../../model_gen/chat";
import RequestOverview from "../../../Request/RequestOverview";
import { DatasetService } from "../../../../services/DatasetService";
import dayjs from "dayjs";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { Skeleton } from "../../../ui/skeleton";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Slider } from "../../../ui/slider";
import { Progress } from "../../../ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { 
  DatabaseIcon,
  CloudUploadIcon,
  FileTextIcon,
  InfoIcon,
  PercentIcon,
  InboxIcon,
  Loader2Icon,
  UploadIcon,
  XIcon,
  CalendarIcon,
  EyeIcon
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { notification } from "antd";

interface DatasetSamplingProps {
  datasetData: DatasetData;
  ignoredApplicationIds: number[];
  onChange: (data: Partial<DatasetData>) => void;
}

const DatasetSampling: React.FC<DatasetSamplingProps> = ({
  datasetData,
  ignoredApplicationIds,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [baseLabel, setBaseLabel] = useState<string>("");
  
  // Manual upload state
  const [dataSourceType, setDataSourceType] = useState<"manual" | "production">("production");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [manualDatasetSize, setManualDatasetSize] = useState<number>(0);
  const [fileAnalyzing, setFileAnalyzing] = useState<boolean>(false);
  const [previewModalVisible, setPreviewModalVisible] = useState<boolean>(false);
  const [previewRequest, setPreviewRequest] = useState<ChatCompletionRequest | null>(null);
  const [formatModalVisible, setFormatModalVisible] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  // Date range state
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const analyzeUploadedFile = async (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      let validRecords = 0;
      const maxSizeForFullAnalysis = 50 * 1024 * 1024; // 50MB limit
      const isLargeFile = file.size > maxSizeForFullAnalysis;
      const sampleSize = isLargeFile ? 1000 : undefined; // Sample first 1000 lines for large files
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split("\n").filter(line => line.trim());
          
          const linesToProcess = sampleSize ? lines.slice(0, sampleSize) : lines;
          
          const processChunk = (startIndex: number) => {
            const chunkSize = 100;
            const endIndex = Math.min(startIndex + chunkSize, linesToProcess.length);
            
            for (let i = startIndex; i < endIndex; i++) {
              try {
                const parsed = JSON.parse(linesToProcess[i]);
                if (parsed.messages && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
                  validRecords++;
                }
              } catch (parseError) {
                // Skip invalid JSON lines
              }
            }
            
            if (endIndex < linesToProcess.length) {
              setTimeout(() => processChunk(endIndex), 0);
            } else {
              // If we sampled, estimate total based on sample
              const finalCount = isLargeFile 
                ? Math.round((validRecords / linesToProcess.length) * lines.length)
                : validRecords;
              resolve(finalCount);
            }
          };
          
          processChunk(0);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  const formatFileSize = (bytes: number): { value: number; unit: string } => {
    if (bytes === 0) return { value: 0, unit: "B" };
    
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return {
      value: parseFloat((bytes / Math.pow(k, i)).toFixed(1)),
      unit: sizes[i]
    };
  };

  const convertJsonlToRequest = (jsonlEntry: any): ChatCompletionRequest => {
    return {
      id: Math.floor(Math.random() * 1000000),
      messages: jsonlEntry.messages || [],
      model: jsonlEntry.model || "unknown",
      created_at: Date.now(),
      application_id: datasetData.application_id || 0,
      intent_id: datasetData.intent_id || 0,
    } as ChatCompletionRequest;
  };

  const handlePreviewFile = async () => {
    if (!selectedFile) return;
    
    setFileAnalyzing(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split("\n").filter(line => line.trim());
          
          // Find first valid record
          for (const line of lines.slice(0, 100)) { // Check first 100 lines
            try {
              const parsed = JSON.parse(line);
              if (parsed.messages && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
                const request = convertJsonlToRequest(parsed);
                setPreviewRequest(request);
                setPreviewModalVisible(true);
                break;
              }
            } catch (parseError) {
              // Skip invalid lines
            }
          }
        } catch (error) {
          console.error("Error parsing file for preview:", error);
          notification.error({
            message: "Preview Error",
            description: "Failed to parse file for preview"
          });
        } finally {
          setFileAnalyzing(false);
        }
      };
      
      reader.onerror = () => {
        setFileAnalyzing(false);
        notification.error({
          message: "File Error",
          description: "Failed to read file"
        });
      };
      
      reader.readAsText(selectedFile);
    } catch (error) {
      console.error("Error reading file:", error);
      setFileAnalyzing(false);
      notification.error({
        message: "File Error",
        description: "Failed to read file"
      });
    }
  };

  const handleDataSourceChange = (type: "manual" | "production") => {
    setDataSourceType(type);
    
    if (type === "manual") {
      // Clear production data when switching to manual
      setTotalRequests(0);
      onChange({ 
        total_requests: 0,
        meta: {
          ...datasetData.meta,
          data_source_type: "manual"
        }
      });
    } else {
      // Clear manual data when switching to production
      setSelectedFile(null);
      setManualDatasetSize(0);
      onChange({ 
        meta: {
          ...datasetData.meta,
          data_source_type: "production",
          uploaded_file: undefined,
          manual_dataset_size: undefined
        }
      });
      // Fetch production data
      fetchRequestCount();
    }
  };

  const handleFileUpload = async (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith(".jsonl")) {
      notification.error({
        message: "Invalid File Type",
        description: "Please upload a JSONL file"
      });
      return;
    }
    
    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      notification.error({
        message: "File Too Large",
        description: "File size must be less than 100MB"
      });
      return;
    }
    
    // Show size warnings
    const maxSizeWarning = 10 * 1024 * 1024; // 10MB
    if (file.size > 50 * 1024 * 1024) { // 50MB
      notification.info({
        message: "Large File",
        description: `File is large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Record count will be estimated to prevent browser issues.`
      });
    } else if (file.size > maxSizeWarning) {
      notification.warning({
        message: "Large File",
        description: `File is large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Analysis may take some time.`
      });
    }
    
    setSelectedFile(file);
    
    // Analyze file to count records
    setFileAnalyzing(true);
    try {
      const recordCount = await analyzeUploadedFile(file);
      setManualDatasetSize(recordCount);
      onChange({ 
        meta: {
          ...datasetData.meta,
          uploaded_file: file,
          manual_dataset_size: recordCount,
          data_source_type: "manual"
        }
      });
      const isEstimated = file.size > 50 * 1024 * 1024; // 50MB limit
      notification.success({
        message: "File Processed",
        description: isEstimated 
          ? `File processed: ~${recordCount} records estimated` 
          : `File analyzed: ${recordCount} valid records found`
      });
    } catch (error) {
      console.error("Error analyzing file:", error);
      notification.error({
        message: "Analysis Error",
        description: "Failed to analyze file. Please ensure it's a valid JSONL format."
      });
      setSelectedFile(null);
      setManualDatasetSize(0);
    } finally {
      setFileAnalyzing(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setManualDatasetSize(0);
    onChange({ 
      meta: {
        ...datasetData.meta,
        uploaded_file: undefined,
        manual_dataset_size: undefined
      }
    });
  };

  // Fetch data when needed parameters are available
  const fetchRequestCount = () => {
    if (
      !apiKey ||
      !(
        (datasetData.base_type === "intent_group" && datasetData.intent_group_id) ||
        (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id)
      )
    ) {
      return;
    }

    setLoading(true);

    // Convert intentGroupId, applicationId, and intentId to the correct format for the API
    const igId = datasetData.base_type === "intent_group" ? datasetData.intent_group_id : null;
    const appId = datasetData.base_type === "application_intent" ? datasetData.application_id : null;
    const intId = datasetData.base_type === "application_intent" ? datasetData.intent_id : null;

    DatasetService.getEstimatedDatasetRequestCount(
      igId,
      appId,
      intId,
      datasetData.date_start,
      datasetData.date_end,
      ignoredApplicationIds,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        console.log("Estimated request count response:", response);
        let total = 0;
        if (response && typeof response.count === "number") {
          total = response.count;
        }
        setTotalRequests(total);
        setLoading(false);
        onChange({ total_requests: total });
      },
      (error: any) => {
        console.error("Error fetching estimated request count:", error);
        setLoading(false);
      }
    );
  };

  // Initialize data source type from existing data
  useEffect(() => {
    if (datasetData.meta?.data_source_type) {
      setDataSourceType(datasetData.meta.data_source_type as "manual" | "production");
    }
    if (datasetData.meta?.manual_dataset_size) {
      setManualDatasetSize(datasetData.meta.manual_dataset_size as number);
    }
    
    // Restore uploaded file information from cache
    if (datasetData.meta?.uploaded_file && datasetData.meta?.data_source_type === "manual") {
      const cachedFile = datasetData.meta.uploaded_file as File;
      setSelectedFile(cachedFile);
    }

    // Initialize date range from datasetData
    if (datasetData.date_start) {
      setStartDate(dayjs(datasetData.date_start).format("YYYY-MM-DD"));
    }
    if (datasetData.date_end) {
      setEndDate(dayjs(datasetData.date_end).format("YYYY-MM-DD"));
    }
  }, [datasetData.meta, datasetData.date_start, datasetData.date_end]);

  // Force production data source when base type is intent_group
  useEffect(() => {
    if (datasetData.base_type === "intent_group" && dataSourceType === "manual") {
      setDataSourceType("production");
      // Clear any manual upload data
      setSelectedFile(null);
      setManualDatasetSize(0);
      onChange({ 
        meta: {
          ...datasetData.meta,
          data_source_type: "production",
          uploaded_file: undefined,
          manual_dataset_size: undefined
        }
      });
    }
  }, [datasetData.base_type, dataSourceType, onChange]);

  // Fetch data when component mounts or when key parameters change
  useEffect(() => {
    if (dataSourceType === "production") {
      fetchRequestCount();
    }
  }, [dataSourceType, datasetData.base_type, datasetData.intent_group_id, datasetData.application_id, datasetData.intent_id, datasetData.date_start, datasetData.date_end, ignoredApplicationIds, apiKey]);

  // Get intent group or application/intent names for display
  useEffect(() => {
    if (!apiKey) return;

    if (datasetData.base_type === "intent_group" && datasetData.intent_group_id) {
      IntentService.getIntentGroup(
        datasetData.intent_group_id,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent group response:", response);
          let name = `Intent Group ${datasetData.intent_group_id}`;
          if (response) {
            name = response.intent_name || response.name || name;
          }
          setBaseLabel(`Intent Group: ${name}`);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseLabel(`Intent Group: ${datasetData.intent_group_id}`);
        }
      );
    } else if (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id) {
      ApplicationService.getApplication(
        datasetData.application_id,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          console.log("Application response:", appResponse);
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${datasetData.application_id}`;

          ApplicationService.getApplicationAction(
            datasetData.application_id,
            datasetData.intent_id,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              console.log("Intent response:", intentResponse);
              const intentName =
                intentResponse?.action_type || `Intent ${datasetData.intent_id}`;
              setBaseLabel(`Application: ${appName}, Intent: ${intentName}`);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseLabel(`Application: ${appName}, Intent: ${datasetData.intent_id}`);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseLabel(`Application: ${datasetData.application_id}, Intent: ${datasetData.intent_id}`);
        }
      );
    }
  }, [datasetData.base_type, datasetData.intent_group_id, datasetData.application_id, datasetData.intent_id, apiKey]);

  const calculateSampleSize = () => {
    if (datasetData.sample_type === "all") {
      return totalRequests;
    } else if (datasetData.sample_type === "random" && datasetData.sample_percentage) {
      return Math.round((totalRequests * datasetData.sample_percentage) / 100);
    } else {
      return 0;
    }
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    
    const startValue = start ? dayjs(start).startOf("day").valueOf() : undefined;
    const endValue = end ? dayjs(end).endOf("day").valueOf() : undefined;
    
    onChange({ 
      date_start: startValue,
      date_end: endValue
    });
    
    // Refresh request count to get updated total for the selected date range
    fetchRequestCount();
  };

  const sampleSize = calculateSampleSize();
  const samplePercentageOfTotal = totalRequests > 0 ? (sampleSize / totalRequests) * 100 : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Data Source & Sampling
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Choose your data source and configure sampling options
        </p>
      </div>

      {/* Selected Base Info Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Selected Base
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              {loading && !baseLabel ? (
                <Skeleton className="h-4 w-48" />
              ) : (
                <div className="text-sm text-foreground font-gc-reg">{baseLabel || "Loading..."}</div>
              )}
            </div>
            {dataSourceType === "production" && totalRequests > 0 && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-maitai-green/10 text-maitai-green">
                  {totalRequests.toLocaleString()} requests
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Source Selection */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <CloudUploadIcon className="w-5 h-5 text-maitai-green" />
            Data Source
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={dataSourceType}
            onValueChange={handleDataSourceChange}
            className="space-y-4"
          >
            {datasetData.base_type === "application_intent" && (
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="manual" id="manual" />
                <div className="space-y-1">
                  <Label htmlFor="manual" className="text-sm font-gc-med text-foreground cursor-pointer">
                    Manual Upload
                  </Label>
                  <p className="text-xs text-muted-foreground font-gc-reg">
                    Upload your own JSONL file with conversation data
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="production" id="production" />
              <div className="space-y-1">
                <Label htmlFor="production" className="text-sm font-gc-med text-foreground cursor-pointer">
                  Production Data Sampling
                </Label>
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Sample from existing production conversation data
                </p>
              </div>
            </div>
          </RadioGroup>
          
          {datasetData.base_type === "intent_group" && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-xs text-blue-800 dark:text-blue-200 font-gc-reg">
                <strong>Note:</strong> Intent Group datasets can only use Production Data Sampling. Manual upload is available for Application/Intent datasets.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Upload Interface */}
      {dataSourceType === "manual" && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <UploadIcon className="w-5 h-5 text-maitai-green" />
                Upload Dataset File
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFormatModalVisible(true)}
                className="gap-2"
              >
                <FileTextIcon className="w-4 h-4" />
                View Format Example
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedFile ? (
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
                  isDragOver 
                    ? "border-maitai-green bg-maitai-green/5" 
                    : "border-border hover:border-maitai-green/50"
                )}
                onDrop={handleFileDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onClick={() => document.getElementById("file-input")?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".jsonl"
                  onChange={handleFileInput}
                  className="hidden"
                />
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    <CloudUploadIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-gc-med text-foreground">
                      Click or drag JSONL file to upload
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Upload a JSONL file where each line contains a conversation with a "messages" array.
                      The file will be analyzed to count valid records.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-maitai-green/5 border border-maitai-green/20 rounded-lg">
                  <div className="w-5 h-5 bg-maitai-green rounded-full flex items-center justify-center mt-0.5">
                    <FileTextIcon className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-gc-med text-foreground">File selected and analyzed</h4>
                        <p className="text-xs text-muted-foreground">
                          {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeFile}
                        className="gap-1"
                      >
                        <XIcon className="w-3 h-3" />
                        Remove
                      </Button>
                    </div>
                    <p className="text-sm text-foreground">
                      Valid records found: <span className="font-gc-med text-maitai-green">{manualDatasetSize}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {fileAnalyzing && (
              <div className="flex items-center gap-2 mt-4">
                <Loader2Icon className="w-4 h-4 animate-spin text-maitai-green" />
                <span className="text-sm text-muted-foreground">Analyzing file...</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Production Data Interface */}
      {dataSourceType === "production" && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <DatabaseIcon className="w-5 h-5 text-maitai-green" />
              Sampling Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sampling Method Selection */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Sampling Method</Label>
                <span className="text-red-500 text-sm">*</span>
              </div>
                             <RadioGroup
                 value={datasetData.sample_type}
                 onValueChange={(value) => onChange({ sample_type: value as "all" | "random" | "none" })}
                 className="space-y-4"
               >
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="all" id="all" />
                  <div className="space-y-1">
                    <Label htmlFor="all" className="text-sm font-gc-med text-foreground cursor-pointer">
                      Use All Requests
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Include all available requests in the dataset
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="random" id="random" />
                  <div className="space-y-1">
                    <Label htmlFor="random" className="text-sm font-gc-med text-foreground cursor-pointer">
                      Random Sample
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Select a random percentage of requests
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="none" id="none" />
                  <div className="space-y-1">
                    <Label htmlFor="none" className="text-sm font-gc-med text-foreground cursor-pointer">
                      None (Empty Dataset)
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Create an empty dataset with no requests
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Date Range Filter */}
            {(datasetData.sample_type === "all" || datasetData.sample_type === "random") && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-maitai-green" />
                  <Label className="text-sm font-gc-med text-foreground">Filter by Date Range</Label>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => handleDateRangeChange(e.target.value, endDate)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => handleDateRangeChange(startDate, e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave both fields empty to include all dates
                </p>
              </div>
            )}

            {/* Sample Percentage */}
            {datasetData.sample_type === "random" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PercentIcon className="w-4 h-4 text-maitai-green" />
                    <Label className="text-sm font-gc-med text-foreground">Sample Percentage</Label>
                    <span className="text-red-500 text-sm">*</span>
                  </div>
                  <span className="text-sm font-gc-med text-maitai-green">
                    {datasetData.sample_percentage}%
                  </span>
                </div>
                <div className="space-y-2">
                  <Slider
                    value={[datasetData.sample_percentage || 100]}
                    onValueChange={(value) => onChange({ sample_percentage: value[0] })}
                    max={100}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Percentage of requests to include in the dataset
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dataset Preview */}
      {(dataSourceType === "production" || (dataSourceType === "manual" && selectedFile)) && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InboxIcon className="w-5 h-5 text-maitai-green" />
                {dataSourceType === "manual" ? "Dataset Preview" : "Dataset Size Preview"}
              </CardTitle>
              {dataSourceType === "manual" && selectedFile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviewFile}
                  disabled={!selectedFile || fileAnalyzing}
                  className="gap-2"
                >
                  {fileAnalyzing ? (
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                  View Sample
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {dataSourceType === "production" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sample Progress</span>
                  <span className="font-gc-med text-foreground">{Math.round(samplePercentageOfTotal)}%</span>
                </div>
                <Progress value={samplePercentageOfTotal} className="h-2" />
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-border bg-card/50">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <DatabaseIcon className="w-4 h-4 text-maitai-green" />
                      <span className="text-sm font-gc-med text-muted-foreground">
                        {dataSourceType === "manual" ? "File Size" : "Total Available"}
                      </span>
                    </div>
                    {dataSourceType === "manual" ? (
                      (() => {
                        const fileSize = formatFileSize(selectedFile?.size || 0);
                        return (
                          <div className="text-2xl font-gc-demi-bold text-maitai-green">
                            {fileSize.value} <span className="text-lg text-muted-foreground">{fileSize.unit}</span>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-2xl font-gc-demi-bold text-maitai-green">
                        {loading ? (
                          <Skeleton className="h-8 w-20" />
                        ) : (
                          <>
                            {totalRequests.toLocaleString()} <span className="text-lg text-muted-foreground">requests</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-border bg-card/50">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <InboxIcon className="w-4 h-4 text-maitai-green" />
                      <span className="text-sm font-gc-med text-muted-foreground">Dataset Size</span>
                    </div>
                    <div className="text-2xl font-gc-demi-bold text-maitai-green">
                      {(dataSourceType === "manual" ? manualDatasetSize : sampleSize).toLocaleString()} <span className="text-lg text-muted-foreground">requests</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Example Modal */}
      <Dialog open={formatModalVisible} onOpenChange={setFormatModalVisible}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>JSONL Format Example</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Each line in your JSONL file should be a valid JSON object with a "messages" array:
            </p>
            <div className="bg-muted p-4 rounded-lg overflow-x-auto">
              <pre className="text-xs font-mono text-foreground whitespace-pre-wrap">
{`{"messages": [
  {"role": "system", "content": "You are a helpful assistant."},
  {"role": "user", "content": "Hello, how are you?"},
  {"role": "assistant", "content": "I'm doing well, thank you! How can I help you today?"}
]}
{"messages": [
  {"role": "user", "content": "What's the weather like?"},
  {"role": "assistant", "content": "I don't have access to current weather data. You might want to check a weather app or website."}
]}`}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setFormatModalVisible(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModalVisible} onOpenChange={setPreviewModalVisible}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dataset Preview</DialogTitle>
          </DialogHeader>
          {previewRequest && (
            <RequestOverview
              request={previewRequest}
              isEditable={false}
              showDetails={false}
              expanded={true}
            />
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewModalVisible(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatasetSampling;
